import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES } from "@/lib/data";

const ease = [0.22, 1, 0.36, 1];

// span classes mapped for bento
const spanMap = {
  wide: "md:col-span-2 md:row-span-1",
  tall: "md:col-span-1 md:row-span-2",
  square: "md:col-span-1 md:row-span-1",
};

const aspectMap = {
  wide: "aspect-[16/9] md:aspect-[16/10]",
  tall: "aspect-[3/4] md:aspect-auto md:h-full",
  square: "aspect-[4/5]",
};

export default function CategoryGrid() {
  return (
    <section
      data-testid="category-grid-section"
      className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24 md:pb-32"
    >
      {/* Section header */}
      <div className="grid grid-cols-12 gap-6 md:gap-10 mb-10 md:mb-16 items-end">
        <div className="col-span-12 md:col-span-6">
          <div className="overline text-[color:var(--ink-soft)] mb-4">§ The Ten Wings</div>
          <h2
            className="font-serif-display leading-[0.95] tracking-tight"
            style={{ fontSize: "clamp(40px, 5.5vw, 88px)" }}
          >
            The archive, <span className="font-italic-serif italic">wing by wing.</span>
          </h2>
        </div>
        <div className="col-span-12 md:col-span-6 md:pl-24">
          <p className="text-[15px] leading-[1.65] text-[color:var(--ink-soft)] max-w-lg">
            Ten interlocking collections. Each one a doorway into the same
            question — <em>who did we come from, and what did they carry?</em>
          </p>
        </div>
      </div>

      {/* Bento grid — clean rounded cards with soft shadows */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 md:auto-rows-[minmax(280px,1fr)] gap-4 md:gap-5 [grid-auto-flow:dense]">
        {CATEGORIES.map((cat, i) => (
          <motion.div
            key={cat.slug}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: (i % 4) * 0.05, ease }}
            className={spanMap[cat.span] || spanMap.square}
          >
            <Link
              to={`/category/${cat.slug}`}
              data-testid={`category-card-${cat.slug}`}
              className="group block h-full p-5 md:p-7 relative overflow-hidden rounded-2xl border border-[color:var(--ink-line)] bg-[color:var(--bone-2)] shadow-sm hover:shadow-lg hover:-translate-y-1"
              style={{ transition: "box-shadow 400ms ease, transform 400ms ease" }}
            >
              {/* Meta row */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="numeric-index text-[color:var(--ink-soft)]">{cat.kicker}</div>
                  <h3
                    className="font-serif-display leading-[0.95] tracking-tight mt-1"
                    style={{ fontSize: "clamp(22px, 2.3vw, 34px)" }}
                  >
                    {cat.name}
                  </h3>
                </div>
                <div className="w-9 h-9 rounded-full border border-[color:var(--ink)] flex items-center justify-center transition-transform duration-500 group-hover:rotate-45 group-hover:bg-[color:var(--ink)] group-hover:text-[color:var(--bone)]">
                  <ArrowUpRight size={16} />
                </div>
              </div>

              {/* Image */}
              <div className={`artifact rounded-xl ${aspectMap[cat.span] || aspectMap.square} border border-[color:var(--ink-line)] overflow-hidden mb-4`}>
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="zoom w-full h-full object-cover"
                  loading="lazy"
                />
              </div>

              {/* Blurb */}
              <p className="text-[13.5px] leading-[1.55] text-[color:var(--ink-soft)] pr-4">
                {cat.blurb}
              </p>

              {/* Footer meta */}
              <div className="mt-4 flex items-center justify-between text-[color:var(--ink-soft)]">
                <span className="numeric-index">{cat.count}</span>
                <span className="numeric-index">↗ Enter wing</span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
